import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import ThemeToggleButton from "./ThemeToggleButton";
import GoogleAd from "./GoogleAd";
import "../styles/Outlet.css";
import "../styles/SelectionTooltip.css";
import { defaultOutlets } from "../data/outletInfo.js";


import {
  loadFromCache,
  saveToCache,
  getTimestamp,
  setTimestamp,
  isFresh,
} from "../utils/cache";

const Outlet = () => {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectionTooltip, setSelectionTooltip] = useState(null);
  const outletContentRef = useRef(null);

  useEffect(() => {
    const outletInfoEntries = Object.values(defaultOutlets);
    const normalizeKey = (value) =>
      (value || "")
        .toString()
        .toLowerCase()
        .replace(/\s+/g, "");
    const outletInfoNames = Object.keys(defaultOutlets);

    const findDefaultInfo = (name, id) => {
      const normalizedName = normalizeKey(name);
      const normalizedId = normalizeKey(id);
      return (
        outletInfoEntries.find(
          (info) => normalizeKey(info?.name) === normalizedName
        ) ||
        outletInfoEntries.find(
          (info) => normalizeKey(info?.id) === normalizedId
        ) ||
        null
      );
    };

    const normalizeOutlet = (outlet) => {
      if (!outlet) return null;
      const defaultInfo = findDefaultInfo(outlet.name, outlet.id);
      const biasCategory =
        outlet.biasCategory ||
        outlet?.bias?.category ||
        defaultInfo?.bias?.category ||
        null;
      return {
        ...outlet,
        biasCategory,
      };
    };

    const processAndFilterOutlets = (outletArray) => {
      const filtered = outletArray
        .map(normalizeOutlet)
        .filter(
          (o) => {
            if (!o) return false;
            const nameMatch =
              o.name && outletInfoNames.includes(o.name);
            const normalizedName = normalizeKey(o.name);
            const infoMatch = outletInfoEntries.find((info) => {
              if (!info) return false;
              if (info.name && normalizeKey(info.name) === normalizedName) {
                return true;
              }
              if (info.id && normalizeKey(info.id) === normalizeKey(o.id)) {
                return true;
              }
              return false;
            });
            return Boolean(nameMatch || infoMatch);
          }
        );
      setOutlets(filtered);
    };

    const cached = loadFromCache("cachedOutlets");
    if (cached && isFresh(getTimestamp("outletsTimestamp"))) {
      const arr = Array.isArray(cached) ? cached : Object.values(cached);
      processAndFilterOutlets(arr);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch("/api/outlets/compact");
        if (!res.ok) {
          throw new Error("서버 오류");
        }
        const data = await res.json();
        const fetchedArray = Array.isArray(data) ? data : [];
        const outletsData = {};
        fetchedArray.forEach((doc) => {
          if (doc?.id) {
            outletsData[doc.id] = {
              ...doc,
              biasCategory:
                doc.biasCategory ||
                findDefaultInfo(doc.name, doc.id)?.bias?.category ||
                null,
            };
          }
        });
        saveToCache("cachedOutlets", outletsData);
        setTimestamp("outletsTimestamp");
        processAndFilterOutlets(Object.values(outletsData));
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    })();
  }, []);

  const hideSelectionTooltip = useCallback(() => {
    setSelectionTooltip(null);
  }, []);

  useEffect(() => {
    const handleMouseUp = () => {
      window.setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          hideSelectionTooltip();
          return;
        }

        const rawText = selection.toString().trim();
        if (!rawText) {
          hideSelectionTooltip();
          return;
        }

        const normalizedText = rawText.replace(/\s+/g, " ");
        if (normalizedText.length > 48) {
          hideSelectionTooltip();
          return;
        }
        const words = normalizedText.split(" ");
        if (words.length > 8) {
          hideSelectionTooltip();
          return;
        }

        if (selection.rangeCount === 0) {
          hideSelectionTooltip();
          return;
        }

        const range = selection.getRangeAt(0);
        const containerNode =
          range.commonAncestorContainer?.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentNode
            : range.commonAncestorContainer;

        if (
          !outletContentRef.current ||
          !containerNode ||
          !outletContentRef.current.contains(containerNode)
        ) {
          hideSelectionTooltip();
          return;
        }

        const rects = range.getClientRects();
        const rect =
          rects.length > 0
            ? rects[rects.length - 1]
            : range.getBoundingClientRect();

        if (!rect || (rect.width === 0 && rect.height === 0)) {
          hideSelectionTooltip();
          return;
        }

        const windowWidth =
          window.innerWidth || document.documentElement.clientWidth;

        const tooltipWidth = 280;
        const tooltipHeight = 44;

        const preferredTop = rect.top - tooltipHeight - 12;
        const top = preferredTop > 0 ? preferredTop : rect.bottom + 12;

        let left = rect.left + rect.width / 2;
        left = Math.max(tooltipWidth / 2 + 16, left);
        left = Math.min(windowWidth - tooltipWidth / 2 - 16, left);

        setSelectionTooltip({
          text: normalizedText,
          top,
          left,
        });
      }, 0);
    };

    const handleKeyUp = (event) => {
      if (event.key === "Escape") {
        hideSelectionTooltip();
      }
    };

    const handleScroll = () => {
      hideSelectionTooltip();
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hideSelectionTooltip]);

  if (loading) return <div className="outlet-container">불러오는 중...</div>;
  if (error) return <div className="outlet-container">에러: {error}</div>;

  const getBiasCategory = (outlet) =>
    outlet.biasCategory || outlet?.bias?.category || null;

  const isBroadcastOutlet = (outlet) => {
    if (!outlet) return false;
    const fallback = defaultOutlets[outlet.name] || {};
    const typeValue =
      (outlet.type || fallback.type || fallback.profile?.발행_형태 || "")
        .toString()
        .toLowerCase();
    return typeValue.includes("방송");
  };

  const sortByName = (list) =>
    [...list].sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "", "ko-KR")
    );

  const categorizeByBias = (list) => {
    const progressive = sortByName(list.filter((o) => getBiasCategory(o) === "진보"));
    const moderate = sortByName(list.filter((o) => getBiasCategory(o) === "중도"));
    const conservative = sortByName(list.filter((o) => getBiasCategory(o) === "보수"));
    const unknown = sortByName(
      list.filter(
        (o) =>
          !getBiasCategory(o) ||
          !["진보", "중도", "보수"].includes(getBiasCategory(o))
      )
    );
    return { progressive, moderate, conservative, unknown };
  };

  const outletsByBias = categorizeByBias(outlets);

  const renderOutletCard = (outlet, idx) => (
    <Link to={`/outlet/${outlet.name}`} key={idx} className="outlet-card-link">
      <div
        className={`outlet-card bias-${
          (getBiasCategory(outlet) || "unknown").toLowerCase()
        }`}
      >
        <div className="outlet-card-body">
          <h4 className="outlet-name">{outlet.name}</h4>
        </div>
      </div>
    </Link>
  );

  const renderBiasGrid = (groups) =>
    ["progressive", "moderate", "conservative"].map((biasKey) => (
      <div className="outlet-grid-column" key={biasKey}>
        <h3 className={`outlet-bias-title ${biasKey}`}>
          {biasKey === "progressive" && "진보 성향"}
          {biasKey === "moderate" && "중도 성향"}
          {biasKey === "conservative" && "보수 성향"}
        </h3>
        <div className="outlet-card-container">
          {groups[biasKey].map(renderOutletCard)}
        </div>
      </div>
    ));

  return (
    <>
      <div className="outlet-container" ref={outletContentRef}>
        <h2 className="outlet-page-title">언론사 정보</h2>
        <div className="outlet-grid">{renderBiasGrid(outletsByBias)}</div>
        {outletsByBias.unknown.length > 0 && (
          <div className="outlet-unknown-section">
            <h3 className="outlet-bias-title unknown">성향 정보 없음/모름</h3>
            <div className="outlet-grid-unknown">
              {outletsByBias.unknown.map(renderOutletCard)}
            </div>
          </div>
        )}
        {selectionTooltip && (
          <div
            className="selection-tooltip"
            style={{
              top: `${selectionTooltip.top}px`,
              left: `${selectionTooltip.left}px`,
            }}
          >
            <span className="selection-tooltip__label">
              {selectionTooltip.text.length > 18
                ? `${selectionTooltip.text.slice(0, 18)}…`
                : selectionTooltip.text}
            </span>
            <a
              href={`https://namu.wiki/w/${encodeURIComponent(
                selectionTooltip.text
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="selection-tooltip__link"
              onClick={() => {
                window.setTimeout(() => {
                  hideSelectionTooltip();
                }, 0);
              }}
            >
              나무위키에서 보기
            </a>
          </div>
        )}
      </div>
      <ThemeToggleButton/>
      <ScrollToTop />
    </>
  );
};

export default Outlet;
